import React from 'react';
import * as Yup from 'yup';
import { Stack } from '@mui/material';
import { isDefined, isEmptyString } from 'lib/typeguards';
import { domainNameSchema } from 'lib/validation/domain';
import { TextFormField } from 'lib/form/fields/TextFormField';
import logger from 'lib/logger';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getPublicAndPrivateSettings } from 'store/settings/settingsSlice';
import { EditableSettingsSection } from './EditableSettingsSection';
import { LabeledField } from './SetupSectionPrimitives';

interface FormValues {
    domainName: string;
}

interface WebAppLinksSectionProps {
    isReadOnly?: boolean;
}

const isSet = (value?: string) => isDefined(value) && !isEmptyString(value);

const validationSchema = Yup.object().shape({
    domainName: domainNameSchema().required('Web App Domain is required.')
});

export const WebAppLinksSection: React.FC<WebAppLinksSectionProps> = ({ isReadOnly = false }) => {
    const settings = useAppSelector(state => state.settings.settings);
    const { claims } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();

    const domainName = settings?.web?.domainName;
    const isConfigured = isSet(domainName);

    const initialValues = React.useMemo<FormValues>(() => ({ domainName: domainName ?? '' }), [domainName]);

    const handleSave = React.useCallback(
        async (values: FormValues) => {
            try {
                await new LocationSettingsApi().updatePublicAndPrivateSettings(
                    { web: { domainName: values.domainName } },
                    undefined,
                    true
                );
                dispatch(getPublicAndPrivateSettings(claims));
                return true;
            } catch (error) {
                logger.error(`Error updating web app links: ${JSON.stringify(error)}`);
                return false;
            }
        },
        [claims, dispatch]
    );

    const renderFields = React.useCallback(
        (isEditing: boolean) => (
            <Stack spacing={2}>
                <LabeledField label="Web App Domain">
                    <TextFormField
                        name="domainName"
                        disabled={!isEditing}
                        placeholder="—"
                        showHelperText={isEditing}
                    />
                </LabeledField>
            </Stack>
        ),
        []
    );

    return (
        <EditableSettingsSection<FormValues>
            title="Web app links"
            subtitle="Synced with settings. This is required to begin distributing web links."
            isConfigured={isConfigured}
            isReadOnly={isReadOnly}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSave={handleSave}
            renderFields={renderFields}
        />
    );
};
